import type { Audience, AuthResult, OtpChallenge } from '@pidelo/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../http/api-error.ts';
import { useCountdown } from '../hooks/use-countdown.ts';
import { useApiClient } from '../react/api-context.tsx';

export function webDeviceName(userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent): string {
  const browser = /Edg\//.test(userAgent) ? 'Edge' : /Firefox\//.test(userAgent) ? 'Firefox' : /Chrome\//.test(userAgent) ? 'Chrome' : /Safari\//.test(userAgent) ? 'Safari' : 'Navegador';
  const system = /iPhone|iPad/.test(userAgent) ? 'iPhone' : /Android/.test(userAgent) ? 'Android' : /Mac OS X/.test(userAgent) ? 'Mac' : /Windows/.test(userAgent) ? 'Windows' : null;
  return system ? `${browser} en ${system}` : browser;
}
export function useEmailLogin<A extends Audience>({ audience, onSignedIn, deviceName }: { audience: A; onSignedIn: (result: Extract<AuthResult, { audience: A }>) => void; deviceName?: string }) {
  const client = useApiClient();
  const passwordless = audience === 'ADMIN' || audience === 'BUSINESS';
  const [link] = useState(() => {
    const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
    const challengeId = params.get('challenge'), code = params.get('code');
    return passwordless && challengeId && /^\d{6}$/.test(code ?? '') ? { challengeId, code: code! } : null;
  });
  const [step, setStep] = useState<'login' | 'register' | 'reset' | 'code'>(link ? 'code' : 'login');
  const [email, setEmail] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(link ? { challengeId: link.challengeId, expiresInSeconds: 0, resendInSeconds: 0 } : null);
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const busy = useRef(false);
  const signedIn = useRef(onSignedIn); signedIn.current = onSignedIn;
  const wait = useCountdown(resendAt, { serverClock: false });
  useEffect(() => {
    if (!link) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('challenge'); url.searchParams.delete('code');
    window.history.replaceState(window.history.state, '', url);
  }, [link]);
  const run = useCallback(async (task: () => Promise<void>) => {
    if (busy.current) return;
    busy.current = true; setPending(true); setError(null); setMessage(null);
    try { await task(); } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('bad_response', 'No pudimos continuar. Intenta de nuevo.', 0);
      const seconds = Number((failure.details as { retryAfterSeconds?: number } | undefined)?.retryAfterSeconds);
      if (seconds > 0) setResendAt(Date.now() + seconds * 1000);
      setError(failure);
    } finally { busy.current = false; setPending(false); }
  }, []);
  const authenticate = (path: string, data: object) => run(async () => {
    const scope = client.getSessionScope();
    const result = await client.post<AuthResult>(`/auth/${path}`, { ...data, audience, platform: 'web', deviceName: deviceName ?? webDeviceName() });
    client.assertSession(scope);
    if (result.audience !== audience) throw new ApiError('forbidden', 'Este correo no tiene acceso aquí.', 403);
    signedIn.current(result as Extract<AuthResult, { audience: A }>);
  });
  const ask = async (address: string) => {
    const result = await client.post<OtpChallenge>(passwordless ? '/auth/login/request' : '/auth/password/request', { email: address, audience });
    setEmail(address); setChallenge(result); setResendAt(Date.now() + result.resendInSeconds * 1000); setStep('code');
  };
  return { step, email, challenge, pending, error, message, passwordless, initialCode: link?.code ?? '',
    clearError: () => setError(null),
    changeStep: (next: typeof step) => { setStep(next); setError(null); setMessage(null); },
    login: (address: string, password: string) => authenticate('login', { email: address, password }),
    register: (address: string, password: string) => authenticate('register', { email: address, password }),
    requestReset: (address: string) => run(() => ask(address)),
    requestCode: (address: string) => run(() => ask(address)),
    verifyCode: (code: string) => challenge ? authenticate('login/verify', { challengeId: challenge.challengeId, code }) : Promise.resolve(),
    resendCode: () => run(() => ask(email)),
    reset: (code: string, password: string) => run(async () => { if (!challenge) return; await client.post('/auth/password/reset', { challengeId: challenge.challengeId, code, password }); setStep('login'); setMessage('Contraseña guardada. Ya puedes entrar.'); }),
    resendIn: wait.totalSeconds, resendLabel: wait.label, canResend: wait.totalSeconds === 0 && !pending,
  };
}
export type EmailLogin = ReturnType<typeof useEmailLogin>;
